// CarryOutPage 配下で共有する UI 非依存の純関数群。

import Time from "../../../util/Time";
import { IsAnyDeleted } from "../../../util/DataExtensions";
import { mostFrequentMaterialLabel } from "../../../util/materialLabel";
import { carryOutRow } from "../../../schema/console/carry_out";
import { MaterialMasterBase } from "../../../schema/generated/MaterialMaster";
import { MaterialDetailBase } from "../../../schema/generated/MaterialDetail";
import { WorkBase } from "../../../schema/generated/Work";
import { WorkSubtypeBase } from "../../../schema/generated/WorkSubtype";
import { WorkDetailBase } from "../../../schema/generated/WorkDetail";
import { MixRatioDefinitionBase } from "../../../schema/generated/MixRatioDefinition";
import { MixRatioDetailBase } from "../../../schema/generated/MixRatioDetail";

export type LotRow = carryOutRow["Lots"][number];
export type MaterialsById = Map<number, MaterialMasterBase>;

export function normalizeMaterialId(materialId: unknown): number | null {
  if (materialId == null || materialId === "") {
    return null;
  }
  const num = Number(materialId);
  return Number.isFinite(num) ? num : null;
}

export function materialName(materialsById: MaterialsById, materialId: number | null | undefined): string {
  const normalizedId = normalizeMaterialId(materialId);
  if (normalizedId == null) {
    return "-";
  }
  return materialsById.get(normalizedId)?.name ?? `#${normalizedId}`;
}

// 材料ごとの構成細目(materialDetailsマスタ派生・参考情報)。QRとは紐付かない。
type ConstituentInfo = { constituents: string; capacity: string };
export type ConstituentsById = Map<number, ConstituentInfo>;
export function buildConstituents(details: MaterialDetailBase[]): ConstituentsById {
  const byMat = new Map<number, MaterialDetailBase[]>();
  details.filter((d) => d.deleted_at == null).forEach((d) => {
    const materialId = normalizeMaterialId(d.material_id);
    if (materialId == null) {
      return;
    }
    const arr = byMat.get(materialId) ?? [];
    arr.push(d);
    byMat.set(materialId, arr);
  });
  const result: ConstituentsById = new Map();
  byMat.forEach((arr, mid) => {
    const sorted = [...arr].sort((a, b) => a.sort_order - b.sort_order);
    result.set(mid, {
      constituents: sorted.map((d) => d.detail_name).join(" + "),
      capacity: sorted.map((d) => d.content_quantity != null ? `${d.detail_name} ${d.content_quantity}${d.content_unit}` : null).filter(Boolean).join(" + "),
    });
  });
  return result;
}

export function constituentsOf(constituentsById: ConstituentsById, materialId: number | null | undefined): string {
  const normalizedId = normalizeMaterialId(materialId);
  if (normalizedId == null) {
    return "";
  }
  return constituentsById.get(normalizedId)?.constituents ?? "";
}

// 材料ごとの構成細目マスタ明細 (sort_order 昇順)。ロット詳細の構成細目テーブル用。
export type DetailsByMaterial = Map<number, MaterialDetailBase[]>;
export function buildDetailsByMaterial(details: MaterialDetailBase[]): DetailsByMaterial {
  const byMat: DetailsByMaterial = new Map();
  details.filter((d) => d.deleted_at == null).forEach((d) => {
    const materialId = normalizeMaterialId(d.material_id);
    if (materialId == null) {
      return;
    }
    const arr = byMat.get(materialId) ?? [];
    arr.push(d);
    byMat.set(materialId, arr);
  });
  byMat.forEach((arr) => arr.sort((a, b) => a.sort_order - b.sort_order));
  return byMat;
}

export function detailsOf(detailsByMaterial: DetailsByMaterial, materialId: number | null | undefined): MaterialDetailBase[] {
  const normalizedId = normalizeMaterialId(materialId);
  if (normalizedId == null) {
    return [];
  }
  return detailsByMaterial.get(normalizedId) ?? [];
}

// 構成細目テーブルのセル表示 (荷姿 / 容量)。未設定は "-"。
export function packageFormText(detail: MaterialDetailBase): string {
  if (detail.package_form && detail.package_form !== "-") {
    return detail.package_form;
  }
  return "-";
}

export function capacityText(detail: MaterialDetailBase): string {
  if (detail.content_quantity == null) {
    return "-";
  }
  return `${detail.content_quantity}${detail.content_unit ?? ""}`;
}

// ロットの貼付済QR数が予定(material_count)に達していれば出荷済とみなす。
export function isLotShipped(lot: LotRow): boolean {
  return lot.material_count > 0 && lot.Materials.length === lot.material_count;
}

export function activeLots(co: carryOutRow): LotRow[] {
  return co.Lots.filter((lot) => !IsAnyDeleted(lot));
}

export function plannedTotal(co: carryOutRow): number {
  return activeLots(co).reduce((sum, lot) => sum + lot.material_count, 0);
}

export function carryOutStatusText(co: carryOutRow): string {
  const lots = activeLots(co);
  if (lots.length === 0) {
    return "出荷前（ロット未登録）";
  }
  const shippedLots = lots.filter(isLotShipped);
  if (shippedLots.length === lots.length) {
    const max = shippedLots.reduce((m, l) => Math.max(m, l.shipped_at ?? 0), 0);
    return `出荷済(${new Time(max).Format("YYYY/MM/dd HH:mm:ss")})`;
  }
  if (shippedLots.length > 0) {
    return `出荷中(${shippedLots.length}/${lots.length}ロット完了)`;
  }
  return "出荷前";
}

// 出荷日グループのキー。全ロット出荷済なら出荷日、未達なら(未出荷)、日付欠損なら(出荷日未記録)。
export function shippedGroupKey(co: carryOutRow): string {
  const lots = activeLots(co);
  if (lots.length === 0 || !lots.every(isLotShipped)) {
    return "(未出荷)";
  }
  const maxShipped = lots.reduce((max, lot) => Math.max(max, lot.shipped_at ?? 0), 0);
  if (maxShipped === 0) {
    return "(出荷日未記録)";
  }
  return new Time(maxShipped).Format("YYYY/MM/dd");
}

export function isCarryOutShipped(co: carryOutRow): boolean {
  const lots = activeLots(co);
  return lots.length > 0 && lots.every(isLotShipped);
}

export function carryOutShippedAtEpoch(co: carryOutRow): number {
  if (!isCarryOutShipped(co)) {
    return 0;
  }
  return activeLots(co).reduce((max, lot) => Math.max(max, lot.shipped_at ?? 0), 0);
}

export function attachedTotal(co: carryOutRow): number {
  return activeLots(co).reduce((sum, lot) => sum + lot.Materials.length, 0);
}

// 最頻材料の名称 + 余剰種類数サフィックス。出荷サマリの1チップ用。
export function mainMaterialLabel(co: carryOutRow, materialsById: MaterialsById): string {
  const materialIds = activeLots(co).map((lot) => normalizeMaterialId(lot.material_id));
  return mostFrequentMaterialLabel(materialIds, (id) => materialsById.get(id)?.name ?? "?");
}

// 工事→材料の2段フィルタに必要な派生マスタ一式。
export type WorkMaterialFilterData = {
  works: WorkBase[];
  workSubtypes: WorkSubtypeBase[];
  workDetails: WorkDetailBase[];
  mixRatioDefinitions: MixRatioDefinitionBase[];
  mixRatioDetails: MixRatioDetailBase[];
  materialDetails: MaterialDetailBase[];
};

// 工事の使用予定材料id集合を導出する(work → area_division_id → WorkSubtype → WorkDetail → MixRatioDefinition/Detail → material_id)。
// 工事未選択時は絞込なし(undefined)。
export function allowedMaterialIdsForWork(workFilterData: WorkMaterialFilterData, workId: number | ""): Set<number> | undefined {
  if (workId === "") {
    return undefined;
  }
  const work = workFilterData.works.find((w) => w.id === workId);
  if (!work) {
    return undefined;
  }
  const areaDivisionId = work.area_division_id;
  const subtypeIds = new Set(workFilterData.workSubtypes.filter((s) => s.area_division_id === areaDivisionId).map((s) => s.id));
  const details = workFilterData.workDetails.filter((d) => subtypeIds.has(d.work_subtype_id));
  const materialIds = new Set<number>();
  details.forEach((d) => {
    if (d.mix_ratio_definition_id == null) {
      return;
    }
    const def = workFilterData.mixRatioDefinitions.find((m) => m.id === d.mix_ratio_definition_id);
    if (def) {
      materialIds.add(def.material_id);
    }
    workFilterData.mixRatioDetails
      .filter((md) => md.mix_ratio_definition_id === d.mix_ratio_definition_id)
      .forEach((md) => {
        const materialDetail = workFilterData.materialDetails.find((x) => x.id === md.material_detail_id);
        if (materialDetail) {
          materialIds.add(materialDetail.material_id);
        }
      });
  });
  return materialIds;
}
